import React from "react"

import * as Models from "@/redux/models"
import type { BoxProps } from "@/theme"
import {
  AppIcons,
  AppText,
  Box,
  Button,
  Divider,
  ListItem,
  Pressable,
  Row,
  TitleWithIcon,
  getIconForInstallationType,
} from "@/theme"

import type { InstallationType } from "@/base/sensor-configurations"
import { formatInstallationType } from "@/base/sensor-configurations"
import { usePermissionCheckedAction } from "@/hooks/usePermissionCheckedAction"

import type { CreateConnectionRouteParams } from "@/components/navigate"
import type { DeviceConfiguration } from "@/redux/device-configurations.reducer"
import type { RootState } from "@/redux/root.reducer"
import { useRootSelector } from "@/redux/useRootSelector"
import i18n from "@/translations/i18n"
import { useRouter } from "expo-router"
import { createSelector } from "reselect"

const getDeviceId = (_: RootState, deviceId: string) => deviceId

const getListItems = createSelector(
  Models.deviceConfiguration.selectEntities,
  Models.deviceConnection.selectAll,
  getDeviceId,
  (configurations, allConnections, deviceId) => {
    const result: Array<{
      description: string
      deviceInstallationType: InstallationType
      id: number
    }> = []

    for (const connection of allConnections) {
      let otherConfig: DeviceConfiguration | undefined
      if (
        connection.sourceDeviceId === deviceId &&
        typeof connection.targetDeviceId === "string"
      ) {
        otherConfig = configurations[connection.targetDeviceId]
      } else if (connection.targetDeviceId === deviceId) {
        otherConfig = configurations[connection.sourceDeviceId]
      }
      if (otherConfig) {
        const installationTypeFormatted: string = formatInstallationType(
          otherConfig.deviceInstallationType,
        )
        let description = `${
          otherConfig.deviceName ?? otherConfig.codaDeviceAlias
        } (${installationTypeFormatted})`
        // Call the device "self" if it's the same device
        if (otherConfig.deviceId === deviceId) {
          description = `${i18n.t("self")}`
        }
        result.push({
          description,
          deviceInstallationType: otherConfig.deviceInstallationType,
          id: connection.id,
        })
      }
    }
    return result
  },
)

function CreateConnectionButton(props: CreateConnectionRouteParams) {
  const router = useRouter()
  const withPermissions = usePermissionCheckedAction()
  const text = i18n.t("deviceConnections:newConnection")
  const handlePress = withPermissions({
    callback: () =>
      router.navigate({
        params: props,
        pathname: "/device-connections/create",
      }),
    required: "canManageCustomTriggers",
  })
  return (
    <Button
      IconComponent={"Add"}
      role={"link"}
      size={"sm"}
      text={text}
      variant={"text"}
      onPress={handlePress}
    />
  )
}

/**
 * List of connections for a device
 */
export function DeviceConnectionsMiniList({
  initiatorDeviceId,
  onPressDeviceConnection,
  showHeading = true,
  ...rest
}: BoxProps & {
  initiatorDeviceId: string
  onPressDeviceConnection: (params: { deviceConnectionId: number }) => void
  showHeading?: boolean
}) {
  const connections = useRootSelector((state) => {
    return getListItems(state, initiatorDeviceId)
  })
  const headerElement = (
    <React.Fragment>
      <Row alignItems={"center"} justifyContent={"space-between"}>
        <Box flex={1}>
          <TitleWithIcon
            IconComponent={AppIcons.DeviceConnection}
            _headingProps={{ variant: "h6" }}
            titleText={i18n.t("deviceConnections:connectionsListTitle")}
          />
        </Box>
        <CreateConnectionButton initiatorDeviceId={initiatorDeviceId} />
      </Row>
      <Divider my={"$1"} />
    </React.Fragment>
  )

  if (connections.length > 0) {
    return (
      <Box {...rest}>
        {headerElement}
        {connections.map((connection) => {
          return (
            <Pressable
              key={connection.id}
              trackHover
              trackPress
              onPress={() =>
                onPressDeviceConnection({ deviceConnectionId: connection.id })
              }
            >
              <ListItem
                text={connection.description}
                IconComponent={getIconForInstallationType(
                  connection.deviceInstallationType,
                )}
              />
            </Pressable>
          )
        })}
      </Box>
    )
  }
  return (
    <Box {...rest}>
      {headerElement}
      <Row alignItems={"center"}>
        <Box mr={"$2"}>
          <AppIcons.Warning />
        </Box>
        <Box flex={1} minW={"$64"}>
          <AppText>{i18n.t("devices:noConnections")}</AppText>
        </Box>
      </Row>
    </Box>
  )
}
